"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { usePasskey } from "@/contexts/PasskeyContext";
import {
  ADAPTER_IDS,
  TESTNET_TOKENS,
  POOL_ADDRESSES,
  TOKEN_UNIT,
  TOKEN_DECIMALS,
  getContractExplorerUrl,
  getTxExplorerUrl,
} from "@/lib/hoops-contracts";
import type { LpPlan, RedeemPlan } from "@/lib/hoops-passkey-kit";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Check, Loader2, Lock, ExternalLink, Fingerprint, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";

// ---- Types ----

type StepId = "connect" | "deploy" | "fund" | "swap" | "deposit" | "withdraw" | "claim";
type StepStatus = "locked" | "active" | "in-progress" | "completed";

interface LogEntry {
  id: string;
  timestamp: Date;
  level: "info" | "success" | "error";
  message: string;
  txHash?: string;
}

const STEP_ORDER: StepId[] = ["connect", "deploy", "fund", "swap", "deposit", "withdraw", "claim"];

const STEP_META: Record<StepId, { number: number; title: string; description: string }> = {
  connect: { number: 1, title: "Create / Connect Passkey", description: "Authenticate with biometrics to create or reconnect your wallet" },
  deploy: { number: 2, title: "Deploy Smart Account", description: "Your passkey deploys an on-chain Hoops account" },
  fund: { number: 3, title: "Fund with XLM", description: "Fund your smart account on testnet" },
  swap: { number: 4, title: "Swap XLM \u2192 USDC", description: "Swap via router \u2014 passkey signs via __check_auth" },
  deposit: { number: 5, title: "Deposit into LP", description: "Provide liquidity to Aqua & Soroswap (50/50 split)" },
  withdraw: { number: 6, title: "Withdraw from LP", description: "Redeem LP + auto-claim Aqua rewards" },
  claim: { number: 7, title: "Claim Rewards", description: "Claim any remaining Aqua LP rewards" },
};

// ---- Helpers ----

function formatBalance(stroops: bigint): string {
  const whole = stroops / BigInt(TOKEN_UNIT);
  const frac = stroops % BigInt(TOKEN_UNIT);
  const fracStr = frac.toString().padStart(TOKEN_DECIMALS, "0").slice(0, 2);
  return `${whole.toString()}.${fracStr}`;
}

// ---- Main Component ----

export function DemoFlow() {
  const {
    contractId,
    isConnected,
    isLoading,
    error: passkeyError,
    createWallet,
    connectWallet,
    fundWallet,
    swap,
    deposit,
    batchRedeem,
    redeemAqua,
    claimAqua,
    queryBalance,
    disconnect,
  } = usePasskey();

  const [stepStatuses, setStepStatuses] = useState<Record<StepId, StepStatus>>({
    connect: "active",
    deploy: "locked",
    fund: "locked",
    swap: "locked",
    deposit: "locked",
    withdraw: "locked",
    claim: "locked",
  });
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [balances, setBalances] = useState({ xlm: "0.00", usdc: "0.00", lpAqua: "0.00", lpSoroswap: "0.00" });
  const [userName, setUserName] = useState("");

  const busyRef = useRef(false);

  const addLog = useCallback((level: LogEntry["level"], message: string, txHash?: string) => {
    setLogs((prev) => [...prev, { id: crypto.randomUUID(), timestamp: new Date(), level, message, txHash }]);
  }, []);

  const completeStep = useCallback((stepId: StepId) => {
    setStepStatuses((prev) => {
      const next = { ...prev, [stepId]: "completed" as StepStatus };
      const idx = STEP_ORDER.indexOf(stepId);
      if (idx < STEP_ORDER.length - 1) {
        const nextStep = STEP_ORDER[idx + 1];
        if (next[nextStep] === "locked") next[nextStep] = "active";
      }
      return next;
    });
  }, []);

  const setStepProcessing = useCallback((stepId: StepId) => {
    setStepStatuses((prev) => ({ ...prev, [stepId]: "in-progress" as StepStatus }));
  }, []);

  const setStepActive = useCallback((stepId: StepId) => {
    setStepStatuses((prev) => ({ ...prev, [stepId]: "active" as StepStatus }));
  }, []);

  // When passkey connects, advance past connect + deploy steps
  useEffect(() => {
    if (isConnected && contractId) {
      setStepStatuses((prev) => {
        if (prev.connect === "completed") return prev;
        return {
          ...prev,
          connect: "completed",
          deploy: "completed",
          fund: "active",
        };
      });
    }
  }, [isConnected, contractId]);

  // Balance refresh — delegates to kit query methods
  const refreshBalances = useCallback(async () => {
    if (!contractId) return;
    try {
      const [xlmBal, usdcBal, lpAquaBal, lpSoroswapBal] = await Promise.all([
        queryBalance(TESTNET_TOKENS.XLM),
        queryBalance(TESTNET_TOKENS.USDC),
        queryBalance(POOL_ADDRESSES.AQUA_LP_TOKEN),
        queryBalance(POOL_ADDRESSES.SOROSWAP_PAIR),
      ]);

      setBalances({
        xlm: formatBalance(xlmBal),
        usdc: formatBalance(usdcBal),
        lpAqua: formatBalance(lpAquaBal),
        lpSoroswap: formatBalance(lpSoroswapBal),
      });
    } catch { /* ignore */ }
  }, [contractId, queryBalance]);

  useEffect(() => {
    if (!contractId) return;
    void refreshBalances();
    const interval = setInterval(() => { void refreshBalances(); }, 10000);
    return () => { clearInterval(interval); };
  }, [contractId, refreshBalances]);

  // ---- Actions ----

  const handleCreateWallet = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("connect");
    addLog("info", "Registering passkey \u2014 authenticate with your biometric...");

    try {
      const result = await createWallet("Hoops Finance", userName || "user");
      if (result) {
        addLog("success", `Contract deployed: ${result.contractId.slice(0, 16)}...`, result.deployHash);
        addLog("success", `Initialized with passkey`, result.initHash);
      } else {
        addLog("error", passkeyError || "Wallet creation failed");
        setStepActive("connect");
      }
    } catch (err) {
      addLog("error", err instanceof Error ? err.message : "Failed to create wallet");
      setStepActive("connect");
    } finally {
      busyRef.current = false;
    }
  }, [userName, createWallet, passkeyError, addLog, setStepProcessing, setStepActive]);

  const handleConnectWallet = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("connect");
    addLog("info", "Reconnecting from stored session...");

    try {
      await connectWallet();
    } catch (err) {
      addLog("error", err instanceof Error ? err.message : "Failed to reconnect");
      setStepActive("connect");
    } finally {
      busyRef.current = false;
    }
  }, [connectWallet, addLog, setStepProcessing, setStepActive]);

  const handleFundWallet = useCallback(async () => {
    if (!contractId || busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("fund");
    addLog("info", "Funding smart account via Friendbot...");

    try {
      const result = await fundWallet(TESTNET_TOKENS.XLM);
      if (result?.success) {
        addLog("success", `Funded with ${String(result.amount ?? "~9900")} XLM! (balance verified on-chain)`, result.hash);
        completeStep("fund");
        void refreshBalances();
      } else {
        addLog("error", result?.error || "Funding failed");
        setStepActive("fund");
      }
    } catch (err) {
      addLog("error", err instanceof Error ? err.message : "Funding failed");
      setStepActive("fund");
    } finally {
      busyRef.current = false;
    }
  }, [contractId, fundWallet, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  const handleSwap = useCallback(async () => {
    if (!contractId || busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("swap");

    try {
      // Swap ~4,900 XLM (roughly half of Friendbot funding) to USDC
      const xlmToSwap = 4_900n * BigInt(TOKEN_UNIT);
      const deadline = Math.floor(Date.now() / 1000) + 600;

      addLog("info", "Swapping ~4,900 XLM \u2192 USDC via Soroswap \u2014 authenticate with biometric...");

      const result = await swap(
        TESTNET_TOKENS.XLM,
        TESTNET_TOKENS.USDC,
        xlmToSwap,
        POOL_ADDRESSES.SOROSWAP_PAIR,
        deadline,
      );

      if (result?.success) {
        addLog("success", "Swap completed!", result.hash);
        completeStep("swap");
        void refreshBalances();
      } else {
        addLog("error", result?.error || "Swap failed");
        setStepActive("swap");
      }
    } catch (err) {
      addLog("error", err instanceof Error ? err.message : "Swap failed");
      setStepActive("swap");
    } finally {
      busyRef.current = false;
    }
  }, [contractId, swap, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  const handleDeposit = useCallback(async () => {
    if (!contractId || busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("deposit");

    const deadline = Math.floor(Date.now() / 1000) + 600;

    try {
      // Query actual balances to determine deposit amounts
      const [usdcBal, xlmBal] = await Promise.all([
        queryBalance(TESTNET_TOKENS.USDC),
        queryBalance(TESTNET_TOKENS.XLM),
      ]);

      const MIN_USDC = BigInt(TOKEN_UNIT) / 2n; // 0.5 USDC
      const XLM_RESERVE = 10n * BigInt(TOKEN_UNIT); // Keep 10 XLM for fees

      if (usdcBal < MIN_USDC) {
        addLog("error", `Insufficient USDC: ${formatBalance(usdcBal)}. Need at least 0.5 USDC.`);
        setStepActive("deposit");
        busyRef.current = false;
        return;
      }

      const availableXlm = xlmBal > XLM_RESERVE ? xlmBal - XLM_RESERVE : 0n;
      if (availableXlm <= 0n) {
        addLog("error", `Insufficient XLM after reserving 10 XLM for fees. Balance: ${formatBalance(xlmBal)}`);
        setStepActive("deposit");
        busyRef.current = false;
        return;
      }

      // 50/50 split: half to Aqua, half to Soroswap
      const halfUsdc = usdcBal / 2n;
      const halfXlm = availableXlm / 2n;
      const totalUsdc = halfUsdc * 2n; // ensure even split for total

      addLog("info", `Depositing 50/50 split across Aqua & Soroswap`);
      addLog("info", `  Aqua: ${formatBalance(halfUsdc)} USDC + ${formatBalance(halfXlm)} XLM`);
      addLog("info", `  Soroswap: ${formatBalance(halfUsdc)} USDC + ${formatBalance(halfXlm)} XLM`);

      const plans: LpPlan[] = [
        {
          tokenA: TESTNET_TOKENS.USDC,
          tokenB: TESTNET_TOKENS.XLM,
          amountA: halfUsdc,
          amountB: halfXlm,
          adapterId: BigInt(ADAPTER_IDS.AQUA),
          poolAddress: POOL_ADDRESSES.AQUA_POOL,
        },
        {
          tokenA: TESTNET_TOKENS.USDC,
          tokenB: TESTNET_TOKENS.XLM,
          amountA: halfUsdc,
          amountB: halfXlm,
          adapterId: BigInt(ADAPTER_IDS.SOROSWAP),
          poolAddress: POOL_ADDRESSES.SOROSWAP_PAIR,
        },
      ];

      addLog("info", "Depositing into Aqua & Soroswap \u2014 authenticate with biometric...");
      const result = await deposit(TESTNET_TOKENS.USDC, totalUsdc, plans, deadline);

      if (result?.success) {
        addLog("success", "Multi-protocol deposit complete!", result.hash);
        completeStep("deposit");
        void refreshBalances();
      } else {
        addLog("error", result?.error || "Deposit failed");
        setStepActive("deposit");
      }
    } catch (err) {
      addLog("error", err instanceof Error ? err.message : "Deposit failed");
      setStepActive("deposit");
    } finally {
      busyRef.current = false;
    }
  }, [contractId, deposit, queryBalance, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  const handleWithdraw = useCallback(async () => {
    if (!contractId || busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("withdraw");
    addLog("info", "Querying LP positions...");

    try {
      const deadline = Math.floor(Date.now() / 1000) + 600;

      const [aquaShares, soroswapShares] = await Promise.all([
        queryBalance(POOL_ADDRESSES.AQUA_LP_TOKEN),
        queryBalance(POOL_ADDRESSES.SOROSWAP_PAIR),
      ]);

      addLog("info", `LP (Aqua): ${formatBalance(aquaShares)}, LP (Soroswap): ${formatBalance(soroswapShares)}`);

      if (aquaShares === 0n && soroswapShares === 0n) {
        addLog("info", "No LP positions to withdraw.");
        completeStep("withdraw");
        busyRef.current = false;
        return;
      }

      // Separate TXs per protocol. Aqua uses direct pool.withdraw() to bypass
      // the Router/Adapter path whose LP transfers trigger expensive checkpoint
      // hooks that exceed the Soroban budget.

      if (soroswapShares > 0n) {
        addLog("info", "TX 1: Withdrawing from Soroswap \u2014 authenticate with biometric...");
        const soroswapResult = await batchRedeem(
          [{
            lpToken: POOL_ADDRESSES.SOROSWAP_PAIR,
            lpAmount: soroswapShares,
            adapterId: BigInt(ADAPTER_IDS.SOROSWAP),
            poolAddress: POOL_ADDRESSES.SOROSWAP_PAIR,
          }],
          deadline,
        );
        if (soroswapResult?.success) {
          addLog("success", "Soroswap withdrawal complete!", soroswapResult.hash);
        } else {
          addLog("error", soroswapResult?.error || "Soroswap withdrawal failed");
          setStepActive("withdraw");
          busyRef.current = false;
          return;
        }
        void refreshBalances();
      }

      if (aquaShares > 0n) {
        addLog("info", `TX ${soroswapShares > 0n ? "2" : "1"}: Withdrawing from Aqua (direct pool call) \u2014 authenticate with biometric...`);
        const aquaResult = await redeemAqua(POOL_ADDRESSES.AQUA_POOL, aquaShares);
        if (aquaResult?.success) {
          addLog("success", "Aqua withdrawal complete!", aquaResult.hash);

          // Auto-claim Aqua rewards (no biometric needed — pool.claim has no require_auth)
          addLog("info", "Claiming Aqua LP rewards (no biometric needed)...");
          const claimResult = await claimAqua(POOL_ADDRESSES.AQUA_POOL);
          if (claimResult?.success) {
            addLog("success", "Aqua rewards claimed!", claimResult.hash);
          } else {
            // Non-fatal — rewards may not have accrued yet on a fresh pool
            addLog("info", "No Aqua rewards to claim (pool may be too new).");
          }
        } else {
          addLog("error", aquaResult?.error || "Aqua withdrawal failed");
          setStepActive("withdraw");
          busyRef.current = false;
          return;
        }
      }

      addLog("success", "All LP positions redeemed!");
      completeStep("withdraw");
      void refreshBalances();
    } catch (err) {
      addLog("error", err instanceof Error ? err.message : "Withdraw failed");
      setStepActive("withdraw");
    } finally {
      busyRef.current = false;
    }
  }, [contractId, batchRedeem, redeemAqua, claimAqua, queryBalance, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  const handleClaim = useCallback(async () => {
    if (!contractId || busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("claim");
    addLog("info", "Claiming Aqua LP rewards (direct pool call, no biometric needed)...");

    try {
      const result = await claimAqua(POOL_ADDRESSES.AQUA_POOL);

      if (result?.success) {
        addLog("success", "Aqua rewards claimed!", result.hash);
        completeStep("claim");
        void refreshBalances();
      } else {
        // Claim fails gracefully when no rewards have accrued
        addLog("info", "No rewards to claim (pool may be too new). Demo complete!");
        completeStep("claim");
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "Claim failed";
      addLog("info", `Claim returned: ${errMsg}. Demo complete!`);
      completeStep("claim");
    } finally {
      busyRef.current = false;
    }
  }, [contractId, claimAqua, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  const handleDisconnect = useCallback(() => {
    disconnect();
    setStepStatuses({
      connect: "active",
      deploy: "locked",
      fund: "locked",
      swap: "locked",
      deposit: "locked",
      withdraw: "locked",
      claim: "locked",
    });
    setBalances({ xlm: "0.00", usdc: "0.00", lpAqua: "0.00", lpSoroswap: "0.00" });
    setLogs([]);
    addLog("info", "Disconnected \u2014 localStorage cleared. Ready to create a new wallet.");
  }, [disconnect, addLog]);

  // ---- Render helpers ----

  const renderStepCard = (stepId: StepId, children: React.ReactNode, completedSummary?: React.ReactNode) => {
    const meta = STEP_META[stepId];
    const status = stepStatuses[stepId];
    const isLocked = status === "locked";
    const isCompleted = status === "completed";
    const isProcessing = status === "in-progress";

    return (
      <Card
        key={stepId}
        className={cn(
          "transition-all duration-300",
          isLocked && "opacity-50 pointer-events-none",
          isCompleted && "border-green-500/30",
          isProcessing && "border-primary/50"
        )}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold",
                isCompleted && "bg-green-500/20 text-green-500",
                isProcessing && "bg-primary/20 text-primary",
                status === "active" && "bg-primary/10 text-primary",
                isLocked && "bg-muted text-muted-foreground"
              )}>
                {isCompleted ? <Check className="w-4 h-4" /> : isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : isLocked ? <Lock className="w-3.5 h-3.5" /> : meta.number}
              </div>
              <div>
                <h3 className="font-semibold text-base">{meta.title}</h3>
                <p className="text-sm text-muted-foreground">{meta.description}</p>
              </div>
            </div>
            {isCompleted && <Badge variant="outline" className="text-green-500 border-green-500/30">Complete</Badge>}
            {isProcessing && <Badge className="bg-primary/20 text-primary border-primary/30">Processing</Badge>}
          </div>
        </CardHeader>
        <CardContent>
          {isCompleted && completedSummary ? (
            <div className="text-sm text-muted-foreground">{completedSummary}</div>
          ) : !isLocked ? children : null}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left column — Steps */}
      <div className="flex-1 space-y-4">
        {/* Step 1: Create / Connect Passkey */}
        {renderStepCard(
          "connect",
          <div className="space-y-3">
            <Input
              placeholder="Username (optional)"
              value={userName}
              onChange={(e) => { setUserName(e.target.value); }}
              className="text-sm"
            />
            <div className="flex gap-2">
              <Button onClick={() => void handleCreateWallet()} disabled={isLoading} className="flex-1">
                <Fingerprint className="w-4 h-4 mr-2" />
                {isLoading ? "Deploying..." : "Create Passkey Wallet"}
              </Button>
              <Button variant="secondary" onClick={() => void handleConnectWallet()} disabled={isLoading}>
                Reconnect
              </Button>
            </div>
            {passkeyError && <p className="text-sm text-destructive">{passkeyError}</p>}
            <p className="text-xs text-muted-foreground">
              Deploys our Hoops Account contract with secp256r1 __check_auth.
            </p>
          </div>,
          contractId && (
            <div className="flex items-center gap-2">
              <Fingerprint className="w-4 h-4 text-purple-400" />
              <a
                href={getContractExplorerUrl(contractId)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-mono text-xs text-blue-500 hover:underline"
              >
                {contractId.slice(0, 16)}...{contractId.slice(-8)}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )
        )}

        {/* Step 2: Deploy (auto-completed) */}
        {renderStepCard(
          "deploy",
          <p className="text-sm text-muted-foreground">Smart account is deployed automatically with your passkey.</p>,
          contractId && (
            <span className="text-xs text-muted-foreground">Deployed with passkey creation</span>
          )
        )}

        {/* Step 3: Fund */}
        {renderStepCard(
          "fund",
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Fund your smart account with testnet XLM via Friendbot.</p>
            <Button onClick={() => void handleFundWallet()} disabled={stepStatuses.fund === "in-progress"} className="w-full">
              {stepStatuses.fund === "in-progress" ? "Funding..." : "Fund via Friendbot"}
            </Button>
          </div>,
          <span className="text-xs">Funded with testnet XLM</span>
        )}

        {/* Step 4: Swap XLM → USDC */}
        {renderStepCard(
          "swap",
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Swap XLM to USDC via the Hoops Router. Passkey signs via __check_auth.</p>
            <Button onClick={() => void handleSwap()} disabled={stepStatuses.swap === "in-progress"} className="w-full">
              <Fingerprint className="w-4 h-4 mr-2" />
              {stepStatuses.swap === "in-progress" ? "Swapping..." : "Sign & Swap"}
            </Button>
          </div>,
          <span className="text-xs">Swap step completed</span>
        )}

        {/* Step 5: Deposit (50/50 Aqua + Soroswap) */}
        {renderStepCard(
          "deposit",
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Split USDC + XLM 50/50 across Aqua and Soroswap LP pools.</p>
            <Button onClick={() => void handleDeposit()} disabled={stepStatuses.deposit === "in-progress"} className="w-full">
              <Fingerprint className="w-4 h-4 mr-2" />
              {stepStatuses.deposit === "in-progress" ? "Processing..." : "Sign & Deposit"}
            </Button>
          </div>,
          <span className="text-xs">Deposited into Aqua + Soroswap</span>
        )}

        {/* Step 6: Withdraw (both protocols) */}
        {renderStepCard(
          "withdraw",
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Redeem LP from both protocols. Requires 2 biometric prompts (separate TXs for budget limits).</p>
            <Button onClick={() => void handleWithdraw()} disabled={stepStatuses.withdraw === "in-progress"} className="w-full">
              <Fingerprint className="w-4 h-4 mr-2" />
              {stepStatuses.withdraw === "in-progress" ? "Processing..." : "Sign & Withdraw"}
            </Button>
          </div>,
          <span className="text-xs">Withdrawn from both protocols</span>
        )}

        {/* Step 7: Claim Rewards */}
        {renderStepCard(
          "claim",
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Claim Aqua rewards directly from pool (no biometric needed).</p>
            <Button onClick={() => void handleClaim()} disabled={stepStatuses.claim === "in-progress"} className="w-full">
              <Fingerprint className="w-4 h-4 mr-2" />
              {stepStatuses.claim === "in-progress" ? "Processing..." : "Sign & Claim"}
            </Button>
          </div>,
          <span className="text-xs">Rewards claimed</span>
        )}
      </div>

      {/* Right column — Balances + Logs */}
      <div className="lg:w-[38%] lg:sticky lg:top-6 lg:self-start space-y-4">
        {/* Balance Panel */}
        <Card>
          <CardHeader className="pb-2">
            <h3 className="font-semibold">Smart Account Balances</h3>
            {contractId && (
              <a
                href={getContractExplorerUrl(contractId)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-mono text-xs text-blue-500 hover:underline"
              >
                {contractId.slice(0, 12)}...
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">XLM:</span> <span className="font-mono">{balances.xlm}</span></div>
              <div><span className="text-muted-foreground">USDC:</span> <span className="font-mono">{balances.usdc}</span></div>
              <div><span className="text-muted-foreground">LP (Aqua):</span> <span className="font-mono">{balances.lpAqua}</span></div>
              <div><span className="text-muted-foreground">LP (Soroswap):</span> <span className="font-mono">{balances.lpSoroswap}</span></div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="ghost" size="sm" onClick={() => void refreshBalances()} className="text-xs">
                Refresh
              </Button>
              {isConnected && (
                <Button variant="ghost" size="sm" onClick={handleDisconnect} className="text-xs text-destructive hover:text-destructive">
                  <LogOut className="w-3 h-3 mr-1" />
                  Logout
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Execution Log */}
        <Card>
          <CardHeader className="pb-2">
            <h3 className="font-semibold">Execution Log</h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {logs.length === 0 && <p className="text-xs text-muted-foreground">No activity yet</p>}
              {logs.map((log) => (
                <div key={log.id} className="text-xs border-l-2 pl-2 py-1" style={{
                  borderColor: log.level === "success" ? "#22c55e" : log.level === "error" ? "#ef4444" : "#6b7280"
                }}>
                  <span className="text-muted-foreground">{log.timestamp.toLocaleTimeString()}</span>{" "}
                  <span>{log.message}</span>
                  {log.txHash && (
                    <a
                      href={getTxExplorerUrl(log.txHash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-1 text-blue-500 hover:underline"
                    >
                      [tx]
                    </a>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
